import { useMemo } from 'react';
import type { BodyState } from '@/lib/astro';
import type { SatPosition } from '@/lib/satellites';
import { formatDD, toRad, type GeoPoint } from '@/lib/geo';

interface SkyViewProps {
  location: GeoPoint;
  bodies: BodyState[];
  satellites: SatPosition[];
  selectedSat: string | null;
  onSelectSat: (norad: string | null) => void;
}

const SIZE = 320;
const C = SIZE / 2;
const R = 128;

function polar(az: number, alt: number): { x: number; y: number } {
  // Negative altitudes are allowed (for labels outside the horizon ring).
  const r = R * (1 - Math.min(90, alt) / 90);
  return { x: C + r * Math.sin(toRad(az)), y: C - r * Math.cos(toRad(az)) };
}

const CARDINALS: Array<[string, number]> = [
  ['N', 0],
  ['E', 90],
  ['S', 180],
  ['W', 270],
];
const INTERCARDINALS: Array<[string, number]> = [
  ['NE', 45],
  ['SE', 135],
  ['SW', 225],
  ['NW', 315],
];

/**
 * Polar sky chart for the selected location: zenith in the center, horizon at
 * the edge, azimuth around the circle (north up).
 */
export function SkyView({ location, bodies, satellites, selectedSat, onSelectSat }: SkyViewProps) {
  const bodiesUp = useMemo(() => bodies.filter((b) => b.alt > 0), [bodies]);
  const satsUp = useMemo(
    () =>
      satellites
        .filter((s) => s.aboveHorizon)
        .sort((a, b) => b.el - a.el)
        .slice(0, 60),
    [satellites],
  );

  const spokes = useMemo(() => {
    const lines: Array<{ x1: number; y1: number; x2: number; y2: number }> = [];
    for (let az = 0; az < 360; az += 30) {
      const end = polar(az, 0);
      lines.push({ x1: C, y1: C, x2: end.x, y2: end.y });
    }
    return lines;
  }, []);

  return (
    <div className="space-y-3">
      <svg
        viewBox={`0 0 ${SIZE} ${SIZE}`}
        className="mx-auto w-full max-w-[340px] select-none"
        role="img"
        aria-label={`Sky chart for ${formatDD(location)}`}
      >
        {/* sky dome background */}
        <circle cx={C} cy={C} r={R} fill="var(--muted)" fillOpacity="0.25" />
        <circle cx={C} cy={C} r={R} fill="none" stroke="var(--border)" strokeWidth="1.5" />
        <circle cx={C} cy={C} r={(R * 2) / 3} fill="none" stroke="var(--border)" strokeWidth="0.8" strokeDasharray="3 4" />
        <circle cx={C} cy={C} r={R / 3} fill="none" stroke="var(--border)" strokeWidth="0.8" strokeDasharray="3 4" />
        {spokes.map((l, i) => (
          <line key={i} {...l} stroke="var(--border)" strokeWidth="0.6" strokeOpacity="0.7" />
        ))}

        {/* altitude labels */}
        <text x={C + 4} y={C - R / 3 + 4} fontSize="8" fill="var(--muted-foreground)">60°</text>
        <text x={C + 4} y={C - (R * 2) / 3 + 4} fontSize="8" fill="var(--muted-foreground)">30°</text>

        {/* compass labels */}
        {CARDINALS.map(([label, az]) => {
          const p = polar(az, -14);
          return (
            <text
              key={label}
              x={p.x}
              y={p.y}
              fontSize="12"
              fontWeight="700"
              textAnchor="middle"
              dominantBaseline="middle"
              fill="var(--foreground)"
            >
              {label}
            </text>
          );
        })}
        {INTERCARDINALS.map(([label, az]) => {
          const p = polar(az, -12);
          return (
            <text
              key={label}
              x={p.x}
              y={p.y}
              fontSize="9"
              textAnchor="middle"
              dominantBaseline="middle"
              fill="var(--muted-foreground)"
            >
              {label}
            </text>
          );
        })}

        {/* satellites above the horizon */}
        {satsUp.map((s) => {
          const p = polar(s.az, s.el);
          const isSelected = s.norad === selectedSat;
          return (
            <circle
              key={s.norad}
              cx={p.x}
              cy={p.y}
              r={isSelected ? 6 : s.isIss ? 5 : 3}
              fill={isSelected ? '#fb7185' : s.isIss ? '#fb923c' : '#22d3ee'}
              stroke={isSelected ? '#fff' : 'none'}
              strokeWidth={isSelected ? 1.5 : 0}
              fillOpacity={isSelected || s.isIss ? 1 : 0.85}
              className="cursor-pointer"
              onClick={() => onSelectSat(isSelected ? null : s.norad)}
            >
              <title>{`${s.name} · ${s.el.toFixed(0)}° above horizon`}</title>
            </circle>
          );
        })}

        {/* Sun / Moon / planets above the horizon */}
        {bodiesUp.map((b) => {
          const p = polar(b.az, b.alt);
          if (b.name === 'Sun') {
            return (
              <g key={b.name}>
                <circle cx={p.x} cy={p.y} r={13} fill={b.color} fillOpacity="0.22" />
                <circle cx={p.x} cy={p.y} r={8} fill={b.color} />
                <title>{`Sun · ${b.alt.toFixed(1)}° up`}</title>
              </g>
            );
          }
          if (b.name === 'Moon') {
            return (
              <g key={b.name}>
                <circle cx={p.x} cy={p.y} r={7} fill={b.color} />
                <circle cx={p.x - 2} cy={p.y - 1.5} r={5.4} fill="var(--muted)" fillOpacity="0.45" />
                <title>{`Moon · ${b.alt.toFixed(1)}° up`}</title>
              </g>
            );
          }
          return (
            <g key={b.name}>
              <circle cx={p.x} cy={p.y} r={4} fill={b.color} />
              <text x={p.x + 7} y={p.y + 3} fontSize="9" fontWeight="600" fill="var(--muted-foreground)">
                {b.abbr}
              </text>
              <title>{`${b.name} · ${b.alt.toFixed(1)}° up`}</title>
            </g>
          );
        })}

        {/* zenith marker */}
        <circle cx={C} cy={C} r={1.8} fill="var(--foreground)" />
      </svg>

      {/* legend */}
      <div className="flex flex-wrap items-center justify-center gap-x-4 gap-y-1 text-[11px] text-muted-foreground">
        <span className="inline-flex items-center gap-1.5">
          <span className="size-2.5 rounded-full" style={{ background: '#fbbf24' }} /> Sun
        </span>
        <span className="inline-flex items-center gap-1.5">
          <span className="size-2.5 rounded-full" style={{ background: '#cbd5e1' }} /> Moon
        </span>
        <span className="inline-flex items-center gap-1.5">
          <span className="size-2 rounded-full" style={{ background: '#f0abfc' }} /> Planets
        </span>
        <span className="inline-flex items-center gap-1.5">
          <span className="size-2 rounded-full" style={{ background: '#22d3ee' }} /> Satellites
        </span>
        <span className="inline-flex items-center gap-1.5">
          <span className="size-2.5 rounded-full" style={{ background: '#fb923c' }} /> ISS
        </span>
      </div>

      <p className="text-center text-xs text-muted-foreground">
        {bodiesUp.length} {bodiesUp.length === 1 ? 'body' : 'bodies'} · {satsUp.length} tracked{' '}
        {satsUp.length === 1 ? 'satellite' : 'satellites'} above the horizon at{' '}
        <span className="font-mono">{formatDD(location)}</span>
      </p>
    </div>
  );
}
